import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'react-feather';

/**
 * Multi-select chip picker for delivery partners, matching the
 * `.v3-sadmin-multi` pattern in sadmin.css. Replaces the
 * react-bootstrap-typeahead instance the legacy AddTenant used.
 *
 * Value is an array of `delivery_partner_id` strings. Options are
 * `{delivery_partner_id, delivery_partner_name}` (the shape produced by
 * useTenantLookups.deliveryPartners).
 *
 * The dropdown menu is rendered into document.body via a portal with
 * fixed positioning. The field lives inside a `.v3-card`, which clips with
 * `overflow: hidden` (rounded corners) AND establishes a stacking context
 * via `backdrop-filter` — so an in-card absolutely-positioned menu would
 * be both clipped by its own card and painted under the following card.
 * Portalling to the body escapes both.
 */
const DeliveryPartnersField = ({ value = [], options = [], onChange, placeholder = 'Choose delivery partners…' }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const wrapRef = useRef(null);
    const toggleRef = useRef(null);
    const menuRef = useRef(null);

    const updateRect = useCallback(() => {
        const el = toggleRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.bottom + 4, width: r.width });
    }, []);

    // Position on open; keep aligned to the toggle on scroll/resize.
    useLayoutEffect(() => {
        if (!open) return;
        updateRect();
        const onReflow = () => updateRect();
        window.addEventListener('scroll', onReflow, true);
        window.addEventListener('resize', onReflow);
        return () => {
            window.removeEventListener('scroll', onReflow, true);
            window.removeEventListener('resize', onReflow);
        };
    }, [open, updateRect]);

    // Close on outside click — the menu is portalled, so check it too.
    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const selected = options.filter((o) => value.includes(o.delivery_partner_id));

    const toggle = (id) => {
        if (value.includes(id)) {
            onChange?.(value.filter((v) => v !== id));
        } else {
            onChange?.([...value, id]);
        }
    };

    const removeChip = (e, id) => {
        e.stopPropagation();
        onChange?.(value.filter((v) => v !== id));
    };

    const menu = open && rect
        ? createPortal(
            <div
                ref={menuRef}
                className="v3-sadmin-multi-menu"
                style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, right: 'auto', zIndex: 1000 }}
            >
                {options.length === 0 ? (
                    <div className="v3-sadmin-multi-item" style={{ color: 'var(--v3-text-muted)' }}>
                        {t('No delivery partners available')}
                    </div>
                ) : (
                    options.map((o) => (
                        <label key={o.delivery_partner_id} className="v3-sadmin-multi-item">
                            <input
                                type="checkbox"
                                checked={value.includes(o.delivery_partner_id)}
                                onChange={() => toggle(o.delivery_partner_id)}
                            />
                            <span>{o.delivery_partner_name}</span>
                        </label>
                    ))
                )}
            </div>,
            document.body,
        )
        : null;

    return (
        <div className="v3-sadmin-multi" ref={wrapRef}>
            <button
                type="button"
                ref={toggleRef}
                className={`v3-sadmin-multi-toggle ${open ? 'open' : ''}`}
                onClick={() => setOpen((o) => !o)}
            >
                {selected.length === 0 ? (
                    <span className="placeholder">{t(placeholder)}</span>
                ) : (
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                        {selected.slice(0, 3).map((s) => (
                            <span
                                key={s.delivery_partner_id}
                                className="v3-sadmin-pill"
                                style={{ background: 'var(--v3-bg-elevated)', color: 'var(--v3-text-primary)' }}
                            >
                                {s.delivery_partner_name}
                                <X
                                    size={10}
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => removeChip(e, s.delivery_partner_id)}
                                />
                            </span>
                        ))}
                        {selected.length > 3 && (
                            <span className="v3-sadmin-pill off">{t('+{{count}} more', { count: selected.length - 3 })}</span>
                        )}
                    </span>
                )}
                <ChevronDown size={14} />
            </button>
            {menu}
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--v3-text-muted)' }}>
                {selected.length > 0 ? t('{{count}} selected', { count: selected.length }) : t('Optional')}
            </div>
        </div>
    );
};

export default DeliveryPartnersField;
